/**
 * O veredicto da prova do microfone — a lógica, separada do desenho.
 *
 * Isto existe por uma sessão inteira gasta a arranjar a coisa errada. O sintoma
 * era "o anel não fica verde"; eu tratei-o como problema do indicador e afinei
 * limiares três vezes. Não era: não entrava sinal nenhum. A app não tinha como
 * distinguir as duas coisas, e eu também não — por isso adivinhei, e mal.
 *
 * A primeira versão deste ficheiro repetia o erro em ponto pequeno: tinha um só
 * estado de falha, "não capta nada", e dizia-o a QUALQUER silêncio. Mas um
 * `AudioContext` suspenso também mede zero, e um microfone que o sistema não
 * está a alimentar também — e as três coisas pedem acções opostas. Um
 * diagnóstico que junta causas diferentes numa frase só manda a pessoa trocar
 * de aparelho quando o aparelho não tem nada de errado. Foi o que me aconteceu
 * a mim, três vezes, e é o que estes estados existem para não repetir.
 *
 * O que o browser dá para separar as causas, e que se usa aqui:
 *  - `AudioContext.state === "suspended"` — o medidor é que está parado, não o
 *    microfone. Mede zeros e não prova nada sobre o aparelho.
 *  - `MediaStreamTrack.muted` — não é o mudo do utilizador (esse é `enabled`):
 *    é o browser a dizer que A FONTE não está a entregar amostras. É a
 *    assinatura de o sistema operativo ou outra aplicação ter o aparelho.
 *  - `readyState === "ended"` — o aparelho desapareceu depois de aberto.
 * Só quando nada disto se aplica é que o silêncio é mesmo do microfone.
 */

/** Acima disto há alguma coisa a entrar — nem que seja o ruído da sala. */
export const ALGUM_SINAL = 0.004;

/** Quanto tempo se espera antes de dizer que não entra nada. */
export const PACIENCIA_MS = 3_000;

export type Estado = "a-abrir" | "a-ouvir" | "parado" | "tomado" | "desligado" | "sem-som" | "recusado";

/** O que se sabe do aparelho neste instante, lido do browser. */
export interface Sinais {
  /** O maior nível visto desde que a prova abriu. */
  maximo: number;
  /** Há quanto tempo a prova está aberta. */
  desdeMs: number;
  /** O `AudioContext` está suspenso — mede zeros por si, diga o aparelho o que disser. */
  suspenso: boolean;
  /** `MediaStreamTrack.muted`: a fonte não está a entregar amostras. */
  fonteMuda: boolean;
  /** `readyState === "ended"`: o aparelho foi-se. */
  terminado: boolean;
}

export function veredicto(sinais: Sinais): Estado {
  // Ouvir vem primeiro: uma vez provado que entra som, nenhuma bandeira o
  // desmente. As outras respondem "porque é que não entra", e já entrou.
  if (sinais.maximo > ALGUM_SINAL) return "a-ouvir";
  if (sinais.terminado) return "desligado";
  if (sinais.fonteMuda) return "tomado";
  if (sinais.suspenso) return "parado";
  if (sinais.desdeMs < PACIENCIA_MS) return "a-abrir";
  return "sem-som";
}

/**
 * Cada recado diz a CAUSA e o passo seguinte. "Não capta nada" sozinho manda
 * trocar de aparelho — e se a causa for outra, faz perder a tarde a trocar.
 */
export const RECADO: Record<Estado, string> = {
  "a-abrir": "A ouvir o microfone…",
  "a-ouvir": "O microfone está a captar. Fala e a barra mexe.",
  parado: "O áudio do browser está suspenso. Clica aqui na janela para o acordar.",
  tomado:
    "O sistema não está a entregar som deste microfone — está desligado nas definições do Windows, ou outra aplicação tem-no aberto. Fecha-a e volta a abrir esta janela.",
  desligado: "O microfone deixou de existir a meio (desligado ou removido).",
  "sem-som": "Este microfone abre mas não capta nada. Escolhe outro na lista.",
  recusado: "O browser não deixou abrir este microfone.",
};
